import React, { useEffect, useState } from 'react'
import { Alert, Button, Col, Form, Modal, Row } from '@themesberg/react-bootstrap'
import { useCookies } from 'react-cookie'

import { updateTicketImages } from '../../../../../controller/api/ticket'

const MAX_IMAGE_BYTES = 10 * 1024 * 1024
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.onload = () => resolve(reader.result)
  reader.onerror = reject
  reader.readAsDataURL(file)
})

const UpdateTicketImagesModal = ({ show, ticketId, images, onSaved, onHide }) => {
  const [cookies] = useCookies(['lottmekong'])
  const [draftImages, setDraftImages] = useState(['', ''])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (show) {
      setDraftImages([images?.[0] || '', images?.[1] || ''])
      setError('')
    }
  }, [show, images, ticketId])

  const handleFileChange = async (index, event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      setError('Chỉ chấp nhận ảnh JPEG, PNG, GIF hoặc WebP.')
      return
    }

    if (file.size > MAX_IMAGE_BYTES) {
      setError('Mỗi ảnh vé phải có dung lượng không quá 10MB.')
      return
    }

    try {
      const dataUrl = await readFileAsDataUrl(file)
      setDraftImages(current => current.map((image, imageIndex) => imageIndex === index ? dataUrl : image))
      setError('')
    } catch (readError) {
      console.error('Error reading ticket image:', readError)
      setError('Không đọc được file ảnh đã chọn.')
    }
  }

  const removeImage = (index) => {
    setDraftImages(current => current.map((image, imageIndex) => imageIndex === index ? '' : image))
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')

    try {
      const response = await updateTicketImages(
        cookies.lottmekong,
        ticketId,
        draftImages.filter(Boolean)
      )

      if (response && response.status === 'success') {
        onSaved(ticketId, response.images || [])
        onHide()
        return
      }

      setError((response && response.message) || 'Không thể cập nhật ảnh vé.')
    } catch (saveError) {
      console.error('Error updating ticket images:', saveError)
      setError(saveError.response?.data?.message || 'Không thể cập nhật ảnh vé.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} size="lg" centered>
      <Modal.Header closeButton={!saving}>
        <Modal.Title style={{ fontSize: 18 }}>
          Cập nhật ảnh vé {ticketId}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}

        <Row className="g-3">
          {['Mặt trước', 'Mặt sau'].map((label, index) => (
            <Col md={6} key={label}>
              <Form.Label className="fw-bold">{label}</Form.Label>
              <div
                className="d-flex align-items-center justify-content-center rounded border bg-light mb-2 overflow-hidden"
                style={{ height: 260 }}
              >
                {draftImages[index] ? (
                  <img
                    src={draftImages[index]}
                    alt={`${label} vé ${ticketId}`}
                    className="w-100 h-100"
                    style={{ objectFit: 'contain' }}
                  />
                ) : (
                  <span className="text-muted">Chưa có ảnh</span>
                )}
              </div>
              <div className="d-flex gap-2">
                <Form.Control
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  disabled={saving}
                  onChange={(event) => handleFileChange(index, event)}
                />
                {draftImages[index] && (
                  <Button
                    type="button"
                    variant="outline-danger"
                    disabled={saving}
                    onClick={() => removeImage(index)}
                  >
                    Xoá
                  </Button>
                )}
              </div>
            </Col>
          ))}
        </Row>
        <small className="d-block text-muted mt-3">
          Tối đa 2 ảnh; mỗi ảnh không quá 10MB.
        </small>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={saving} onClick={onHide}>
          Đóng
        </Button>
        <Button variant="primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Đang lưu...' : 'Lưu ảnh vé'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default React.memo(UpdateTicketImagesModal)
